import { NavLink, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Home, FileText, CreditCard, Wrench, FolderOpen,
  ChevronsLeft, ChevronsRight, X, Settings,
} from 'lucide-react'
import { useEffect } from 'react'
import { cn } from '@/lib/utils'
import Logo from '@/components/brand/Logo'

/**
 * Occupant-side sidebar. Deliberately flat — 5 entries, all self-service
 * ("My …"). Mirrors the Pinnacle admin sidebar's motion + hover feel
 * so the two surfaces feel like the same product to a resident who
 * hears about it from their landlord.
 *
 * Nav items reference the /portal/* URL tree that the wire-up agent
 * mounts under an <OccupantLayout /> route in App.jsx.
 */
const NAV = [
  { to: '/portal',             label: 'Home',        icon: Home,        end: true },
  { to: '/portal/lease',       label: 'My Lease',    icon: FileText },
  { to: '/portal/payments',    label: 'My Payments', icon: CreditCard },
  { to: '/portal/maintenance', label: 'Maintenance', icon: Wrench },
  { to: '/portal/documents',   label: 'Documents',   icon: FolderOpen },
  { to: '/portal/settings',    label: 'Settings',    icon: Settings },
]

export default function OccupantSidebar({ collapsed, onToggle, mobileOpen, onMobileClose }) {
  const location = useLocation()

  // Auto-close drawer on route change
  useEffect(() => {
    if (mobileOpen) onMobileClose?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = prev }
    }
  }, [mobileOpen])

  const navContent = (
    <nav className="px-2 py-3 space-y-0.5 overflow-y-auto flex-1">
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            cn(
              'group relative flex items-center gap-3 rounded-md px-3 py-2 transition-all',
              'text-text-muted hover:text-text hover:bg-navy-800',
              isActive && 'text-text bg-navy-800 ring-1 ring-primary/25 shadow-[0_0_24px_-12px_rgba(59,130,246,0.55)]',
              collapsed && 'lg:justify-center lg:px-2',
            )
          }
        >
          {({ isActive }) => (
            <>
              {isActive && (
                <motion.span
                  layoutId="portal-sidebar-active"
                  className="absolute inset-0 rounded-md bg-gradient-to-r from-primary/[0.12] via-primary/[0.03] to-transparent pointer-events-none"
                  transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                />
              )}
              <item.icon className={cn('w-4 h-4 shrink-0 relative z-[1]', isActive && 'text-primary-light')} />
              <span className={cn('text-[13px] flex-1 relative z-[1] font-body', collapsed && 'lg:hidden')}>{item.label}</span>
            </>
          )}
        </NavLink>
      ))}
    </nav>
  )

  const brandHeader = (
    <div className={cn('h-16 lg:h-20 flex items-center px-4 border-b border-navy-600 shrink-0', collapsed && 'lg:justify-center lg:px-2')}>
      <Logo size={collapsed ? 28 : 32} animated />
      <div className={cn('ml-3 leading-none', collapsed && 'lg:hidden')}>
        <div className="font-display text-[15px] font-semibold tracking-tight text-text" style={{ letterSpacing: '-0.02em' }}>Pinnacle</div>
        <div className="font-mono font-medium text-[0.625rem] tracking-[0.22em] uppercase text-text-dim mt-1">Resident Portal</div>
      </div>
      {onMobileClose && (
        <button
          onClick={onMobileClose}
          className="lg:hidden ml-auto p-2 rounded-md text-text-dim hover:text-text hover:bg-navy-800 transition"
          aria-label="Close menu"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  )

  const footer = (
    <div className="p-3 border-t border-navy-600 shrink-0">
      <div className={cn('flex items-center justify-between', collapsed && 'lg:flex-col lg:gap-2')}>
        <div className={cn('flex items-center gap-2 px-2 py-1.5 rounded-md bg-emerald/10 border border-emerald/25', collapsed && 'lg:hidden')}>
          <span className="w-1.5 h-1.5 rounded-full bg-emerald animate-pulse" />
          <span className="font-mono font-medium text-[0.625rem] tracking-[0.22em] uppercase text-emerald">Live</span>
        </div>
        {onToggle && (
          <button
            onClick={onToggle}
            className="hidden lg:inline-flex p-1.5 rounded-md text-text-dim hover:text-text hover:bg-navy-800 transition"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
          </button>
        )}
      </div>
    </div>
  )

  return (
    <>
      <aside
        className={cn(
          'hidden lg:flex flex-col fixed top-0 left-0 h-screen z-20',
          'border-r border-navy-600',
          'bg-navy-900/95 backdrop-blur-2xl',
          'transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]',
          collapsed ? 'w-16' : 'w-60',
        )}
      >
        {brandHeader}
        {navContent}
        {footer}
      </aside>

      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={onMobileClose}
              className="lg:hidden fixed inset-0 bg-navy-950/70 backdrop-blur-sm z-40"
              aria-hidden
            />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className={cn(
                'lg:hidden fixed top-0 left-0 h-screen z-50 w-[280px] flex flex-col',
                'border-r border-navy-600',
                'bg-navy-900/95 backdrop-blur-2xl',
              )}
            >
              {brandHeader}
              {navContent}
              {footer}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  )
}
